import { keccak256, getAddress } from "viem";
import { PasskeyVerificationError, } from "./types.js";
import { extractPublicKeyFromSpki, parseDERPublicKey } from "./encoding.js";
/**
 * Generic passkey verification service
 */
export class PasskeyVerificationService {
    context;
    constructor(context) {
        this.context = context;
    }
    /**
     * Verify a WebAuthn authentication response
     */
    async verifyAuthentication(options) {
        try {
            const { response, expectedChallenge, expectedOrigin, expectedRPID, publicKey, counter, } = options;
            // 1. Verify the response format
            if (!response.id || !response.rawId || !response.response) {
                throw new PasskeyVerificationError("Invalid response format");
            }
            // 2. Verify the challenge
            const clientDataJSON = this.decodeBase64Url(response.response.clientDataJSON);
            const clientData = JSON.parse(new TextDecoder().decode(clientDataJSON));
            if (clientData.type !== "webauthn.get") {
                throw new PasskeyVerificationError("Invalid client data type");
            }
            if (clientData.challenge !== expectedChallenge) {
                throw new PasskeyVerificationError("Challenge mismatch");
            }
            // 3. Verify the origin
            if (clientData.origin !== expectedOrigin) {
                throw new PasskeyVerificationError("Origin mismatch");
            }
            // 4. Verify the rpId
            const authData = this.decodeBase64Url(response.response.authenticatorData);
            const rpIdHash = authData.slice(0, 32);
            const expectedRpIdHash = await this.sha256(expectedRPID);
            if (!this.arraysEqual(rpIdHash, expectedRpIdHash)) {
                throw new PasskeyVerificationError("RP ID hash mismatch");
            }
            // 5. Verify user presence
            const flags = authData[32];
            if ((flags & 0x01) === 0) {
                throw new PasskeyVerificationError("User presence not verified");
            }
            // 6. Verify user verification if required
            if ((flags & 0x04) === 0) {
                throw new PasskeyVerificationError("User verification not verified");
            }
            // 7. Verify signature
            const signature = this.decodeBase64Url(response.response.signature);
            const signedData = new Uint8Array([
                ...authData,
                ...(await this.sha256(clientDataJSON)),
            ]);
            const isValid = await this.verifySignature(publicKey, signedData, signature);
            if (!isValid) {
                throw new PasskeyVerificationError("Invalid signature");
            }
            // 8. Verify counter
            const newCounter = new DataView(authData.buffer, 33, 4).getUint32(0, false);
            if (newCounter < counter) {
                throw new PasskeyVerificationError(`Counter not increased: new=${newCounter}, old=${counter}`);
            }
            return {
                verified: true,
                authenticationInfo: {
                    newCounter,
                    credentialID: response.id,
                },
            };
        }
        catch (error) {
            if (error instanceof PasskeyVerificationError) {
                throw error;
            }
            throw new PasskeyVerificationError(`Verification failed: ${error instanceof Error ? error.message : "Unknown error"}`, "VERIFICATION_FAILED", error);
        }
    }
    /**
     * Derive Ethereum address from public key
     */
    deriveAddressFromPublicKey(publicKeyBase64) {
        try {
            // This uses the same logic as the server-side implementation
            const { x, y } = this.parseDERPublicKey(publicKeyBase64);
            // Combine x and y coordinates (remove 0x prefix if present)
            const xHex = x.replace("0x", "");
            const yHex = y.replace("0x", "");
            const publicKeyHex = xHex + yHex;
            // Hash the public key with Keccak256
            const publicKeyHash = keccak256(("0x" + publicKeyHex));
            // Take the last 20 bytes (40 hex characters) as the address
            const address = "0x" + publicKeyHash.slice(-40);
            return getAddress(address); // Checksum the address
        }
        catch (error) {
            throw new PasskeyVerificationError(`Failed to derive address: ${error instanceof Error ? error.message : "Unknown error"}`, "ADDRESS_DERIVATION_FAILED", error);
        }
    }
    /**
     * Decode base64url string to Uint8Array
     */
    decodeBase64Url(base64url) {
        // Convert base64url to base64
        let base64 = base64url.replace(/-/g, "+").replace(/_/g, "/");
        while (base64.length % 4 !== 0) {
            base64 += "=";
        }
        if (typeof atob !== "undefined") {
            // Browser environment
            const binaryString = atob(base64);
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
                bytes[i] = binaryString.charCodeAt(i);
            }
            return bytes;
        }
        else {
            // Node.js environment
            return new Uint8Array(Buffer.from(base64, "base64"));
        }
    }
    /**
     * SHA-256 hash function (universal)
     */
    async sha256(data) {
        const input = typeof data === "string" ? new TextEncoder().encode(data) : data;
        if (typeof crypto !== "undefined" && crypto.subtle) {
            // Browser environment
            const hashBuffer = await crypto.subtle.digest("SHA-256", input);
            return new Uint8Array(hashBuffer);
        }
        else {
            // Node.js environment
            const { createHash } = await import("crypto");
            const hash = createHash("sha256");
            hash.update(input);
            return new Uint8Array(hash.digest());
        }
    }
    /**
     * Compare two Uint8Arrays for equality
     */
    arraysEqual(a, b) {
        if (a.length !== b.length)
            return false;
        for (let i = 0; i < a.length; i++) {
            if (a[i] !== b[i])
                return false;
        }
        return true;
    }
    /**
     * Verify signature using the appropriate method for the environment
     */
    async verifySignature(publicKeyBase64, signedData, signature) {
        if (this.context.environment === "server") {
            // Server-side verification using Node.js crypto
            return this.verifySignatureServer(publicKeyBase64, signedData, signature);
        }
        else {
            // Client-side verification using Web Crypto API
            return this.verifySignatureClient(publicKeyBase64, signedData, signature);
        }
    }
    /**
     * Server-side signature verification
     */
    async verifySignatureServer(publicKeyBase64, signedData, signature) {
        try {
            const { createVerify } = await import("crypto");
            const pemKey = extractPublicKeyFromSpki(publicKeyBase64);
            const verifier = createVerify("sha256");
            verifier.update(signedData);
            return verifier.verify(pemKey, signature);
        }
        catch (error) {
            throw new PasskeyVerificationError(`Server signature verification failed: ${error instanceof Error ? error.message : "Unknown error"}`, "SERVER_VERIFY_FAILED", error);
        }
    }
    /**
     * Client-side signature verification using Web Crypto API
     */
    async verifySignatureClient(publicKeyBase64, signedData, signature) {
        try {
            if (typeof crypto === "undefined" || !crypto.subtle) {
                throw new Error("Web Crypto API not available");
            }
            // Decode the public key
            const publicKeyDer = this.decodeBase64Url(publicKeyBase64);
            // Import the public key
            const publicKey = await crypto.subtle.importKey("spki", publicKeyDer, {
                name: "ECDSA",
                namedCurve: "P-256",
            }, false, ["verify"]);
            // Verify the signature
            return await crypto.subtle.verify({
                name: "ECDSA",
                hash: "SHA-256",
            }, publicKey, signature, signedData);
        }
        catch (error) {
            throw new PasskeyVerificationError(`Client signature verification failed: ${error instanceof Error ? error.message : "Unknown error"}`, "CLIENT_VERIFY_FAILED", error);
        }
    }
    /**
     * Parse DER public key (uses shared encoding utility)
     */
    parseDERPublicKey(publicKeyBase64) {
        return parseDERPublicKey(publicKeyBase64);
    }
}
/**
 * Factory function to create verification service
 */
export function createVerificationService(context) {
    return new PasskeyVerificationService(context);
}
